import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Subscription } from 'rxjs';
import { catchError, concatMap, first, tap } from 'rxjs/operators';

import { BaseSortableSearchComponent } from '@components/_base/base-search/base-sortable-search.component';
import { SortingPagingData } from '@helpers/sorting-paging-data';
import { AuthenticationService } from '@app/login/_services/authentication.service';
import { PermissionsService } from '@app/login/_services/permissions.service';
import { TechnicalPoint } from '@app/tech-points/_models/technical-point.model';
import { TechnicalPointService } from '@app/tech-points/_services/technical-point.service';
import { ExportService } from '@services/export.service';
import { convertDateToString, convertObjDateToString, todayForDatepicker } from '@app/_utils/date-util';
import { displayError, displayErrorFromUnknown } from '@app/_utils/error-util';

import { ListSale } from '../../_models/list-sale.model';
import { SaleService } from '../../_services/sale.service';

@Component({
  selector: 'app-list-sales',
  templateUrl: './list-sales.component.html',
  styleUrls: ['./list-sales.component.css']
})
export class ListSalesComponent extends BaseSortableSearchComponent<ListSale> implements OnInit, OnDestroy {

  // Units
  technicalPoints: TechnicalPoint[] = [];

  // Payload
  userTechnicalPointMobileAppIds: number[];

  // Booleans
  /** False only for a user with no point of their own, who may search across all of them. */
  hasTechnicalPoint = false;
  loadPdfOrExcel = false;

  // Form
  // The dates open on today, both ends, so the list lands on the current day's takings - which
  // is what a counter is looking at nearly every time it opens this screen. Widening the range
  // is one click on either picker; the pair is filled in rather than blank, so it is visible
  // that a filter is in force.
  searchForm = this.formBuilder.group({
    techPointMobileAppId: [''],
    regNumber:            [null],
    dateFrom:             [todayForDatepicker()],
    dateTo:               [todayForDatepicker()]
  });

  // Observables
  searchSubscription: Subscription;
  search$ = this.searchSubject.asObservable().pipe(
    tap(() => this.dataLoaded = false),
    concatMap(() => this.saleService.pageable(this.sortingPaging, this.searchForm.value).pipe(
      tap(([page, totalCount]) => {
        this.sortingPaging.fromRow = page.fromRow;
        this.sortingPaging.toRow = page.toRow;
        this.sortingPaging.totalElements = totalCount;
        this.content = page.content;
        this.dataLoaded = true;
      })
    )),
    catchError(err => {
      displayError(err);
      this.dataLoaded = false;
      this.errorMessageSubject.next(this.translateService.instant('messages.errorLoadingData'));
      return EMPTY;
    })
  );

  constructor(
    public  perms: PermissionsService,
    private formBuilder: FormBuilder,
    private exportService: ExportService,
    private translateService: TranslateService,
    private technicalPointService: TechnicalPointService,
    private saleService: SaleService
  ) {
    super();
    this.userTechnicalPointMobileAppIds = AuthenticationService.getEmployeeTechPointsIds();
  }

  async ngOnInit() {
    await this.loadTechnicalPoints();
    this.searchSubscription = this.search$.subscribe();
    this.searchSubject.next();
  }

  ngOnDestroy() {
    this.searchSubscription?.unsubscribe();
  }

  /**
   * An employee sees only the point they work at, already chosen; the empty option is withheld
   * from them in the template, so the search cannot be widened past it. A user with no point -
   * head office - gets the full list and chooses.
   */
  private async loadTechnicalPoints() {
    try {
      if (this.userTechnicalPointMobileAppIds.length === 0) {
        this.technicalPoints = await this.technicalPointService.findTechnicalPoints().toPromise();
        this.hasTechnicalPoint = false;
      } else {
        this.technicalPoints =
            await this.technicalPointService.findTechnicalPointsByIds(this.userTechnicalPointMobileAppIds).toPromise();
        this.hasTechnicalPoint = true;
        this.searchForm.get('techPointMobileAppId')?.patchValue(this.technicalPoints[0]?.mobileAppId);
      }
    } catch (err) {
      displayErrorFromUnknown(err);
    }
  }

  clearSearch() {
    this.searchForm.reset();

    // reset() empties every control, so the operator's own point is put back - otherwise
    // clearing would quietly widen the search to every counter.
    if (this.userTechnicalPointMobileAppIds.length !== 0) {
      this.searchForm.get('techPointMobileAppId')?.patchValue(this.technicalPoints[0]?.mobileAppId);
    }

    // Likewise the dates: clearing restores the day the screen opens on, rather than dropping
    // the filter entirely and listing every sale ever made.
    this.searchForm.get('dateFrom')?.patchValue(todayForDatepicker());
    this.searchForm.get('dateTo')?.patchValue(todayForDatepicker());

    this.sortingPaging.pageNumber = 1;
    this.searchSubject.next();
  }

  async exportExcel() {
    this.loadPdfOrExcel = true;
    try {
      this.exportService.exportAsExcelFile(await this.prepareData(), this.getHeadings(),
          this.getFilterBody(), this.getFilterHeading(),
          this.translateService.instant('sales.search.title'));
    } catch (err) {
      displayErrorFromUnknown(err);
    } finally {
      this.loadPdfOrExcel = false;
    }
  }

  async exportPDF() {
    this.loadPdfOrExcel = true;
    try {
      this.exportService.exportAsPdfFile(await this.prepareData(), this.getHeadings(),
          this.getFilterBody(), this.getFilterHeading(),
          this.translateService.instant('sales.search.title'), convertDateToString(new Date()));
    } catch (err) {
      displayErrorFromUnknown(err);
    } finally {
      this.loadPdfOrExcel = false;
    }
  }

  /**
   * Every matching sale, not just the page on screen: the export re-queries with the page size
   * widened to the total. Row order here must match getHeadings().
   */
  private async prepareData() {
    const sortingPagingCopy = new SortingPagingData(this.sortingPaging.totalElements);
    sortingPagingCopy.pageSize = this.sortingPaging.totalElements;
    sortingPagingCopy.sortBy = this.sortingPaging.sortBy;
    sortingPagingCopy.sortAsc = this.sortingPaging.sortAsc;

    const result = await this.saleService.findAllByFilter(sortingPagingCopy, this.searchForm.value)
                             .pipe(first()).toPromise();

    const newContent: any = [];
    result.content.forEach(sale => {
      newContent.push([
        sale.techPointName   ?? '-',
        sale.date            ?? '-',
        sale.regNumber       ?? '-',
        sale.rvsCategoryCode ?? '-',
        sale.serviceNames    || '-',
        sale.itemNames       || '-',
        sale.promoCodes      || '-'
      ]);
    });

    return newContent;
  }

  private getHeadings(): string[][] {
    return [[
      this.translateService.instant('sales.search.techPoint'),
      this.translateService.instant('sales.search.date'),
      this.translateService.instant('sales.search.regNumber'),
      this.translateService.instant('sales.search.rvsCategory'),
      this.translateService.instant('sales.search.services'),
      this.translateService.instant('sales.search.items'),
      this.translateService.instant('sales.search.promoCode')
    ]];
  }

  /** The chosen filters as the operator sees them; aligned with getFilterHeading()[1]. */
  private getFilterBody(): any[] {
    const chosenId = this.searchForm.get('techPointMobileAppId')?.value;
    const techPoint = this.technicalPoints.find(point => `${point.mobileAppId}` === `${chosenId}`);

    return [[
      techPoint?.shortName ?? '',
      this.searchForm.get('regNumber')?.value ?? '',
      convertObjDateToString(this.searchForm.get('dateFrom')?.value),
      convertObjDateToString(this.searchForm.get('dateTo')?.value)
    ]];
  }

  private getFilterHeading(): any[][] {
    return [
      [{
        content: this.translateService.instant('sales.search.title'),
        colSpan: 7,
        styles: { halign: 'center' }
      }],
      [
        this.translateService.instant('sales.search.techPoint'),
        this.translateService.instant('sales.search.regNumber'),
        this.translateService.instant('sales.search.dateFrom'),
        this.translateService.instant('sales.search.dateTo')
      ]
    ];
  }
}
